import Padding from './padding';
import Tilt from './tilt';
import TypewriterTitle from './typewriter-title';

// `app/24/Sponsors.js` — three tiers, each row centred and each logo on its own tilt.
//
// `size` goes on *both* the width and height attributes, as `next/image`'s `width={size}
// height={size}` did, and `h-auto` lets the file's own ratio win. Emitting only `width`
// looks equivalent and is not: `aspect-ratio: auto <w>/<h>` falls back to the attribute
// ratio for an image with no intrinsic one, and `shinkansen-logo-white.svg` has no
// width, no height and no viewBox — so it renders square (205x205) over there and would
// collapse to 205x150 with the height attribute left off.
type SponsorLogo = {
  src: string;
  size: number;
  alt: string;
  url: string;
  className?: string;
};

const MAIN: SponsorLogo[] = [
  {
    src: 'fintual-logo-white.png',
    size: 400,
    alt: 'Fintual logo',
    url: 'https://fintual.cl/',
  },
  {
    src: 'aws-logo-white.png',
    size: 200,
    alt: 'Amazon Web Services logo',
    url: 'https://aws.amazon.com/',
  },
];

const SECONDARY: SponsorLogo[] = [
  {
    src: 'buk-logo-white.png',
    size: 170,
    alt: 'Buk logo',
    url: 'https://buk.cl/',
  },
  {
    src: 'ria-logo.png',
    size: 165,
    alt: 'RIA logo',
    url: 'https://www.riamoneytransfer.com/',
    className: 'filter brightness-0 invert',
  },
];

const TERTIARY: SponsorLogo[] = [
  {
    src: 'ey-logo.svg',
    size: 60,
    alt: 'EY logo',
    url: 'https://ey.com/',
    className: 'grayscale invert pb-8',
  },
  {
    src: 'shinkansen-logo-white.svg',
    size: 205,
    alt: 'Shinkansen logo',
    url: 'https://shinkansen.finance/',
  },
  {
    src: 'pullpo-logo-white.svg',
    size: 125,
    alt: 'Pullpo logo',
    url: 'https://pullpo.io/',
  },
  {
    src: 'fingo-logo-white.svg',
    size: 125,
    alt: 'Fingo logo',
    url: 'https://fingo.cl/',
  },
  {
    src: 'oragus-logo-white.svg',
    size: 125,
    alt: 'Oragus logo',
    url: 'https://oragus.com/',
  },
  {
    src: 'soyio-logo-white.png',
    size: 140,
    alt: 'Soyio logo',
    url: 'https://soyio.id/',
    className: 'grayscale pt-2',
  },
  {
    src: 'salduu-logo-white.svg',
    size: 105,
    alt: 'Salduu logo',
    url: 'https://salduu.com/',
  },
  {
    src: 'mok-logo-white.png',
    size: 105,
    alt: 'Mok logo',
    url: 'https://mok.cl/',
  },
];

const TIERS = [MAIN, SECONDARY, TERTIARY];

export default function Sponsors() {
  return (
    <section className="flex min-h-screen w-full flex-col items-center justify-center pt-24">
      <Padding>
        <TypewriterTitle
          text="$ ls sponsors/*.svg"
          className="mb-12 text-center font-bold font-oxanium text-2xl md:text-5xl"
        />

        <div className="flex flex-col gap-16 py-16 lg:gap-28 lg:py-28">
          {TIERS.map((tier) => (
            <div
              className="flex flex-col flex-wrap items-center justify-center gap-12 lg:flex-row lg:gap-32"
              key={tier[0].src}
            >
              {tier.map(({ src, size, alt, url, className }) => (
                <Tilt
                  key={src}
                  className="parallax-effect"
                  tiltMaxAngleX={10}
                  tiltMaxAngleY={10}
                  perspective={800}
                  transitionSpeed={1500}
                  scale={1.05}
                  gyroscope={true}
                >
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex w-auto items-center justify-center transition-transform duration-300 hover:scale-110"
                  >
                    <img
                      src={`/hack24/sponsors/${src}`}
                      alt={alt}
                      width={size}
                      height={size}
                      className={`h-auto ${className ?? ''}`}
                    />
                  </a>
                </Tilt>
              ))}
            </div>
          ))}
        </div>
      </Padding>
    </section>
  );
}
